import { useState } from "react";
import ReactDOM from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useLogin } from "../../hooks/useLogin";
import {
  GoabMenuAction,
  GoabModal,
  GoabButtonGroup,
  GoabButton,
  GoabMenuButton,
  GoabSpacer,
} from "@abgov/react-components";
import { getLoggedOutEndpoint } from "../../../ServiceUtils/auth";
import { selectCurrentUser } from "../../../Store/api/auth/authSelectors";
import { useGetApplicationListQuery } from "../../../Store/api/application/applicationApi";
import { useAnalyticsStep } from "../../../MPP-Common-SPA/utils/useAnalyticsStep";
import { trackEvent } from "../../../MPP-Common-SPA/utils/analytics";
import {
  SIGN_IN_BUTTON_EVENT,
  SIGN_IN_BUTTON_PARAM_KEY,
} from "../../../MPP-Common-SPA/utils/analytics-constants";
import { useUnsavedChanges } from "../../../MPP-Common-SPA/common/unsavedChangesContext";
import { useIsMobile } from "../../../MPP-Common-SPA/hooks/useIsMobile";

// Longest account label (before the ellipsis) that fits beside the program title
// at ~360px (the Galaxy S25 target) even for wide-letter names like "Mark McDonald".
// Kept conservative because letter widths vary; tunable with design (MPP-5911).
const MOBILE_LABEL_MAX_LENGTH = 8;

const truncateLabel = (value) =>
  value.length > MOBILE_LABEL_MAX_LENGTH
    ? `${value.slice(0, MOBILE_LABEL_MAX_LENGTH).trimEnd()}\u2026`
    : value;

export const HeaderComponent = ({ slot = undefined }) => {
  const { isLoggedIn, givenName, middleName, surname, isProxyUser } =
    useSelector(selectCurrentUser);
  const fullName = `${givenName} ${middleName} ${surname}`;

  // On narrow screens the full name makes the account dropdown wide enough to
  // collide with the program title, so truncate it with an ellipsis there (MPP-5911).
  const isMobile = useIsMobile();
  const accountLabel = isMobile ? truncateLabel(fullName) : fullName;

  const navigate = useNavigate();

  const currentStep = useAnalyticsStep();

  const { data: applicationList, isLoading: isApplicationsLoading } =
    useGetApplicationListQuery(undefined, { skip: !isLoggedIn });

  const shouldShowApplicationsTab =
    !isProxyUser && !isApplicationsLoading && applicationList?.length > 0;

  const { handleLogout, handleLogin } = useLogin();
  const { hasUnsavedChanges, clearDirty } = useUnsavedChanges();
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);

  const handleLoginWithAnalytics = () => {
    trackEvent(SIGN_IN_BUTTON_EVENT, {
      [SIGN_IN_BUTTON_PARAM_KEY]: currentStep,
    });
    handleLogin();
  };

  const handleConfirmSignOut = () => {
    setShowUnsavedModal(false);
    clearDirty();
    if (isProxyUser) {
      handleLogout();
    } else {
      handleLogout(getLoggedOutEndpoint());
    }
  };

  const handleGoToWorkspace = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedModal(true);
    } else {
      handleConfirmSignOut();
    }
  };

  const unsavedChangesModal = showUnsavedModal
    ? ReactDOM.createPortal(
        <GoabModal
          heading="Unsaved changes"
          open={showUnsavedModal}
          maxWidth="480px"
          calloutVariant="important"
        >
          <p>
            You have unsaved changes. If you{" "}
            {!isProxyUser
              ? "sign out,"
              : "leave this page, you will return to your workspace and"}{" "}
            your progress will be lost.
          </p>
          <br />
          <p>
            To continue your application at another time, click "Finish later"
            located at the bottom of your application.
          </p>
          <GoabSpacer vSpacing="xl" />
          <GoabButtonGroup alignment="end">
            <GoabButton
              size="compact"
              type="secondary"
              onClick={() => setShowUnsavedModal(false)}
            >
              Cancel
            </GoabButton>
            <GoabButton
              type="primary"
              variant="destructive"
              size="compact"
              onClick={handleConfirmSignOut}
            >
              {!isProxyUser ? "Sign out" : "Leave page"}
            </GoabButton>
          </GoabButtonGroup>
        </GoabModal>,
        document.body,
      )
    : null;

  return (
    <>
      {isLoggedIn ? (
        <GoabMenuButton
          slot={slot}
          text={accountLabel}
          ariaLabel={fullName}
          leadingIcon="person-circle"
          type="tertiary"
          size="compact"
          onAction={(detail) => {
            if (detail.action === "my-applications") navigate("/applications");
            if (
              detail.action === "sign-out" ||
              detail.action === "go-to-workspace"
            )
              handleGoToWorkspace();
          }}
        >
          {shouldShowApplicationsTab && (
            <GoabMenuAction text="My applications" action="my-applications" />
          )}
          <GoabMenuAction
            text={!isProxyUser ? "Sign out" : "Go to workspace"}
            action={!isProxyUser ? "sign-out" : "go-to-workspace"}
            icon="exit"
          />
        </GoabMenuButton>
      ) : (
        <Link
          to=""
          slot={slot}
          className="interactive"
          onClick={() => handleLoginWithAnalytics()}
          style={{ height: "100%" }}
        >
          Sign in
        </Link>
      )}
      {unsavedChangesModal}
    </>
  );
};
